import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { StatusPill } from '../components/StatusPill.js';
import { VerdictPill } from '../components/VerdictPill.js';
import { friendlyUser, formatDeps, formatDuration } from '../lib/format.js';
import type { Run, PlanResponse } from '../lib/types.js';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

export function RunDetail() {
  const { id = '' } = useParams();
  const query = useApiQuery<Run>(['run', id], `/api/runs/${encodeURIComponent(id)}`);

  if (query.isLoading) return <p>Loading run…</p>;
  if (query.isError || !query.data) return <p className="error">Failed to load run.</p>;

  const run = query.data;
  const te = run.test_env;

  return (
    <section>
      <div className="detail-head">
        <h2>Run {run.run_id}</h2>
        <Link className="btn-link" to={`/runs/${encodeURIComponent(run.run_id)}/logs`}>View logs →</Link>
        <Link className="btn-link" to={`/runs/${encodeURIComponent(run.run_id)}/transcript`}>View transcript →</Link>
      </div>

      <dl className="def-list">
        <Row label="Status"><StatusPill status={run.status} /></Row>
        <Row label="Repo">{run.repo ?? '—'}</Row>
        <Row label="User"><span title={run.user_id ?? undefined}>{friendlyUser(run.user_id)}</span></Row>
        <Row label="Model">{run.model ?? '—'}</Row>
        <Row label="Turns">
          {run.num_turns != null && run.max_turns != null
            ? `${run.num_turns}/${run.max_turns}`
            : run.num_turns != null ? String(run.num_turns) : '—'}
        </Row>
        <Row label="Created">{run.created_at ?? '—'}</Row>
        <Row label="Finished">{run.finished_at ?? '—'}</Row>
        <Row label="Duration">{formatDuration(run.created_at, run.finished_at)}</Row>
        <Row label="Container image">{run.container_image ?? '—'}</Row>
        <Row label="Sidecars">
          {run.sidecars && run.sidecars.length > 0 ? run.sidecars.join(', ') : '—'}
        </Row>
        <Row label="Task ARN">{run.task_arn ?? '—'}</Row>
        <Row label="PR">
          {run.pr_url
            ? <a href={run.pr_url} target="_blank" rel="noopener noreferrer">{run.pr_url}</a>
            : '—'}
        </Row>
        {run.error && <Row label="Error"><span className="error-text">{run.error}</span></Row>}
        {run.wip_branch && (
          <Row label="WIP branch">
            <code>{run.wip_branch}</code>
            <div className="hint">Recover: <code>git fetch origin {run.wip_branch} &amp;&amp; git checkout {run.wip_branch}</code></div>
          </Row>
        )}
      </dl>

      <h3>Test environment</h3>
      {te ? (
        <dl className="def-list">
          <Row label="Verdict"><VerdictPill verdict={te.verdict ?? undefined} /></Row>
          <Row label="Deps">{formatDeps(te.deps)}</Row>
          <Row label="Test DB">{te.test_db ?? '—'}</Row>
          <Row label="Detail">{te.detail ?? '—'}</Row>
        </dl>
      ) : (
        <p className="empty">No test-environment data.</p>
      )}

      <PlanSection runId={run.run_id} />
    </section>
  );
}

function PlanSection({ runId }: { runId: string }) {
  const [open, setOpen] = useState(false);
  const query = useApiQuery<PlanResponse>(['plan', runId], `/api/runs/${encodeURIComponent(runId)}/plan`, {
    enabled: open,
  });
  return (
    <div className="plan-section">
      <h3>
        <button type="button" className="disclosure" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? '▾' : '▸'} Plan
        </button>
      </h3>
      {open && query.isLoading && <p>Loading plan…</p>}
      {open && query.isError && <p className="error">Failed to load plan.</p>}
      {open && !query.isLoading && !query.isError && (
        query.data?.plan ? <pre className="plan-view">{query.data.plan}</pre> : <p className="empty">No plan recorded.</p>
      )}
    </div>
  );
}
